import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { UPLOAD_BASE, fmtDateTH } from './aoShared'

const emptyForm = { cat_id: '', ar_title: '', ar_detail: '', ar_doc_date: '', ar_expire_date: '', ar_location: '' }

// ธุรการ: แฟ้มเอกสารหลักฐานสำคัญโรงเรียน (ตราสาร ใบอนุญาต โฉนด/สัญญา ฯลฯ)
export default function Archive() {
  const [categories, setCategories] = useState([])
  const [catId, setCatId] = useState('')
  const [q, setQ] = useState('')
  const [expiringOnly, setExpiringOnly] = useState(false)
  const [archives, setArchives] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/ao/archive/categories')
      if (res.data.success) setCategories(res.data.data)
    } catch { /* ignore */ }
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (catId) params.set('cat_id', catId)
      if (q) params.set('q', q)
      if (expiringOnly) params.set('expiring_days', '60')
      const res = await axiosInstance.get(`/api/ao/archive?${params.toString()}`)
      if (res.data.success) setArchives(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [catId, q, expiringOnly])

  useEffect(() => { fetchCategories() }, [fetchCategories])
  useEffect(() => { fetchData() }, [fetchData])

  const openCreate = () => {
    setEditId(null)
    setForm({ ...emptyForm, cat_id: catId || (categories[0]?.cat_id ?? '') })
    setFile(null)
    setModalError('')
    setShowModal(true)
  }

  const openEdit = (a) => {
    setEditId(a.ar_id)
    setForm({
      cat_id: a.cat_id,
      ar_title: a.ar_title,
      ar_detail: a.ar_detail || '',
      ar_doc_date: a.ar_doc_date || '',
      ar_expire_date: a.ar_expire_date || '',
      ar_location: a.ar_location || '',
    })
    setFile(null)
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''))
      if (file) fd.append('file', file)
      if (editId) await axiosInstance.put(`/api/ao/archive/${editId}`, fd)
      else await axiosInstance.post('/api/ao/archive', fd)
      setShowModal(false)
      fetchData()
      fetchCategories()
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (a) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ลบเอกสารนี้ออกจากแฟ้ม?',
      text: a.ar_title,
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(`/api/ao/archive/${a.ar_id}`)
      fetchData()
      fetchCategories()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const addCategory = async () => {
    const { value: name } = await Swal.fire({
      title: 'เพิ่มหมวดหมู่เอกสาร',
      input: 'text',
      inputPlaceholder: 'ชื่อหมวดหมู่',
      showCancelButton: true,
      confirmButtonText: 'เพิ่ม',
      cancelButtonText: 'ปิด',
    })
    if (!name) return
    try {
      await axiosInstance.post('/api/ao/archive/categories', { cat_name: name })
      fetchCategories()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เพิ่มไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const today = new Date().toLocaleDateString('en-CA')
  const soonDate = new Date(Date.now() + 60 * 86400000).toLocaleDateString('en-CA')

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-safe-2-line me-2 text-primary" />
              แฟ้มเอกสารหลักฐานสำคัญ
            </h4>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={addCategory}>
                <i className="ri-folder-add-line me-1" />เพิ่มหมวดหมู่
              </Button>
              <Button variant="primary" size="sm" onClick={openCreate}>
                <i className="ri-add-line me-1" />เพิ่มเอกสาร
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 240 }} value={catId} onChange={e => setCatId(e.target.value)}>
                <option value="">ทุกหมวดหมู่</option>
                {categories.map(c => <option key={c.cat_id} value={c.cat_id}>{c.cat_name} ({c.doc_count})</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="ค้นหา ชื่อ/รายละเอียด/ที่เก็บ"
                value={q} onChange={e => setQ(e.target.value)} />
              <Form.Check type="switch" id="expiring-switch" className="fs-sm"
                label="ใกล้หมดอายุ (60 วัน)"
                checked={expiringOnly} onChange={e => setExpiringOnly(e.target.checked)} />
              <div className="ms-auto text-muted fs-sm">{archives.length} รายการ</div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : archives.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบเอกสารในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 190 }}>หมวดหมู่</th>
                      <th>ชื่อเอกสาร</th>
                      <th style={{ width: 110 }}>ลงวันที่</th>
                      <th style={{ width: 140 }}>วันหมดอายุ</th>
                      <th style={{ width: 150 }}>ที่เก็บตัวจริง</th>
                      <th style={{ width: 50 }}>ไฟล์</th>
                      <th style={{ width: 90 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {archives.map(a => {
                      const expired = a.ar_expire_date && a.ar_expire_date < today
                      const expiring = !expired && a.ar_expire_date && a.ar_expire_date <= soonDate
                      return (
                        <tr key={a.ar_id}>
                          <td>{a.cat_name}</td>
                          <td>
                            <span className="fw-semibold">{a.ar_title}</span>
                            {a.ar_detail && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{a.ar_detail}</div>}
                          </td>
                          <td>{fmtDateTH(a.ar_doc_date)}</td>
                          <td>
                            {a.ar_expire_date ? (
                              <>
                                {fmtDateTH(a.ar_expire_date)}
                                {expired && <Badge bg="danger" className="ms-1">หมดอายุ</Badge>}
                                {expiring && <Badge bg="warning" className="ms-1">ใกล้หมดอายุ</Badge>}
                              </>
                            ) : '-'}
                          </td>
                          <td>{a.ar_location || '-'}</td>
                          <td>
                            {a.ar_file ? (
                              <a href={`${UPLOAD_BASE}/uploads/ao_docs/${a.ar_file}`} target="_blank" rel="noreferrer" title="เปิดไฟล์">
                                <i className="ri-attachment-2" />
                              </a>
                            ) : '-'}
                          </td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-primary" className="me-1" title="แก้ไข" onClick={() => openEdit(a)}>
                              <i className="ri-edit-line" />
                            </Button>
                            <Button size="sm" variant="outline-danger" title="ลบ" onClick={() => handleDelete(a)}>
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{editId ? 'แก้ไขเอกสาร' : 'เพิ่มเอกสารสำคัญ'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Row className="g-2 mb-2">
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">หมวดหมู่ <span className="text-danger">*</span></Form.Label>
                <Form.Select size="sm" required value={form.cat_id}
                  onChange={e => setForm(f => ({ ...f, cat_id: e.target.value }))}>
                  <option value="">— เลือก —</option>
                  {categories.map(c => <option key={c.cat_id} value={c.cat_id}>{c.cat_name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={7}>
                <Form.Label className="fs-sm mb-1">ชื่อเอกสาร <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={500} required value={form.ar_title}
                  onChange={e => setForm(f => ({ ...f, ar_title: e.target.value }))} />
              </Col>
            </Row>
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ลงวันที่</Form.Label>
                <Form.Control size="sm" type="date" value={form.ar_doc_date}
                  onChange={e => setForm(f => ({ ...f, ar_doc_date: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">วันหมดอายุ <span className="text-muted">— ใบอนุญาต/สัญญา</span></Form.Label>
                <Form.Control size="sm" type="date" value={form.ar_expire_date}
                  onChange={e => setForm(f => ({ ...f, ar_expire_date: e.target.value }))} />
              </Col>
            </Row>
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">รายละเอียด</Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={form.ar_detail}
                onChange={e => setForm(f => ({ ...f, ar_detail: e.target.value }))} />
            </Form.Group>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ที่เก็บตัวจริง</Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} placeholder="เช่น ตู้เอกสาร 1 แฟ้ม ก-3"
                  value={form.ar_location}
                  onChange={e => setForm(f => ({ ...f, ar_location: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">ไฟล์สแกน</Form.Label>
                <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={e => setFile(e.target.files[0] || null)} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
              บันทึก
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
